/* ExplorationQuiz.jsx — v2 exploration quiz (main + optional mini quizzes)
 * for one Career Clarity branch. One question at a time (same QuizOptions
 * pattern as the After-10th group quizzes). Scoring is server-side; the
 * client only shows the returned result. Saved state is restored from
 * /quiz/state so refresh / back navigation keeps answers and results. */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, ArrowLeft, ArrowRight, CheckCircle2, XCircle, RotateCcw } from 'lucide-react';
import { QuizOptions, QuizSidePanel, QuestionDots } from './QuizStage';
import { Notice } from './GuideSections';

const API = process.env.REACT_APP_BACKEND_URL;
const LETTERS = 'ABCDE';

const STATE_MSG = {
  all_unknown: 'You do not need to choose yet. Try one small activity and return.',
  all_complete_zero: 'None of these examples interests you at present. Explore other options or discuss what you would like to try.',
};

const displayOptions = (q) => q.options.map((o, i) => ({ ...o, optionId: o.id, id: o.id.length === 1 ? o.id : LETTERS[i] }));

/* ── Result view ── */
const DomainList = ({ title, ids, fields, testId }) => ids?.length > 0 && (
  <div data-testid={testId}>
    <div className="text-[11px] font-extrabold uppercase tracking-widest text-gray-500 mb-1.5">{title}</div>
    <div className="flex flex-wrap gap-2">{ids.map((id) => <span key={id} className="px-3 py-1.5 rounded-full bg-white border border-gray-200 text-sm font-semibold text-gray-900">{fields?.find((f) => f.id === id)?.title || id}</span>)}</div>
  </div>
);

const KnowledgeFeedback = ({ k, quiz }) => (
  <div className="space-y-3" data-testid="xq-knowledge">
    <div className="text-sm font-bold text-gray-900">Learning examples: {k.correct} correct of {k.attempted} attempted ({k.total} total, {k.not_attempted} not attempted)</div>
    {k.feedback.map((f) => {
      const q = quiz.questions.find((x) => x.id === f.question_id);
      const opt = (id) => q?.options.find((o) => o.id === id)?.text || id;
      return (
        <div key={f.question_id} className="rounded-xl border border-gray-200 bg-white p-3" data-testid={`xq-kfb-${f.question_id}`}>
          <p className="text-sm font-semibold text-gray-900 leading-snug">{f.prompt}</p>
          <p className="text-xs mt-1.5 flex items-start gap-1.5">{f.attempted ? (f.correct ? <CheckCircle2 size={14} className="text-emerald-600 shrink-0 mt-0.5" /> : <XCircle size={14} className="text-rose-600 shrink-0 mt-0.5" />) : null}
            <span className="text-gray-700">{f.attempted ? <>Your answer: {opt(f.your_option_id)}</> : 'Not attempted (not sure yet)'}</span></p>
          <p className="text-xs text-gray-800 mt-1"><span className="font-semibold">Correct answer:</span> {opt(f.correct_option_id)}</p>
          <p className="text-xs text-gray-600 mt-1 leading-relaxed">{f.explanation}</p>
        </div>
      );
    })}
  </div>
);

const ResultView = ({ saved, quiz, fields, notice, onRetake, onBack }) => {
  const r = saved.result;
  return (
    <div className="space-y-4" data-testid="xq-result">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] font-extrabold uppercase tracking-widest text-teal-700">{quiz.kind === 'main' ? 'Your exploration result' : 'Reflection recap'}</div>
        <span className="text-xs text-gray-500">Saved · attempt {saved.attempts}</span>
      </div>
      {quiz.kind === 'main' ? (
        <>
          {STATE_MSG[r.state] && <p className="text-base font-semibold text-gray-900" data-testid="xq-state-message">{STATE_MSG[r.state]}</p>}
          <DomainList title="Explore next" ids={r.explore_next} fields={fields} testId="xq-explore-next" />
          <DomainList title="Other completed fields" ids={r.other_completed} fields={fields} testId="xq-other-completed" />
          <DomainList title="More to explore" ids={r.more_to_explore} fields={fields} testId="xq-more-to-explore" />
          <div className="grid gap-2 sm:grid-cols-2" data-testid="xq-domain-scores">
            {r.domains.map((d) => <div key={d.domain_id} className="rounded-xl bg-white border border-gray-200 px-3 py-2 text-sm flex justify-between gap-2"><span className="text-gray-800">{fields?.find((f) => f.id === d.domain_id)?.title || d.domain_id}</span><span className="font-bold text-gray-900">{d.complete ? `${d.score} / 4` : 'incomplete'}</span></div>)}
          </div>
        </>
      ) : (
        <div data-testid="xq-reflection">
          <div className="text-sm font-bold text-gray-900">Reflection total: {r.reflection.total === null ? 'not shown (one or more items unknown)' : `${r.reflection.total} / ${r.reflection.max}`}</div>
          <ul className="mt-2 space-y-1">{r.reflection.items.map((it) => { const q = quiz.questions.find((x) => x.id === it.question_id); return <li key={it.question_id} className="text-xs text-gray-700 leading-relaxed"><span className="font-semibold">{q?.prompt}</span> — {q?.options.find((o) => o.id === it.option_id)?.text}</li>; })}</ul>
        </div>
      )}
      <KnowledgeFeedback k={r.knowledge} quiz={quiz} />
      {quiz.result_action && <p className="text-sm text-gray-700 leading-relaxed"><span className="font-semibold">After the quiz:</span> {quiz.result_action}</p>}
      {notice && <Notice testId="xq-result-notice">{notice}</Notice>}
      <div className="flex flex-wrap gap-2 pt-1">
        <button type="button" onClick={onRetake} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-gray-900 text-white text-sm font-bold hover:bg-gray-800" data-testid="xq-retake-btn"><RotateCcw size={14} /> Revise answers</button>
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-gray-300 text-sm font-bold text-gray-800 hover:bg-gray-50" data-testid="xq-back-btn"><ArrowLeft size={14} /> All quizzes</button>
      </div>
    </div>
  );
};

/* ── Runner ── */
const Runner = ({ branch, quiz, saved, accent, onSaved, onBack }) => {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(saved?.responses || {});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const q = quiz.questions[idx];
  const opts = useMemo(() => displayOptions(q), [q]);
  const selected = opts.find((o) => o.optionId === answers[q.id]);
  const last = idx === quiz.questions.length - 1;

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/api/career-clarity/guidance/${branch}/quiz/submit`, { quiz_id: quiz.id, responses: answers });
      onSaved(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || 'Could not save your answers.'); } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-[260px_1fr] gap-4" data-testid="xq-runner">
      <QuizSidePanel grad={accent.grad} eyebrow={quiz.kind === 'main' ? 'Exploration quiz' : 'Optional mini quiz'} title={quiz.title} index={idx} total={quiz.questions.length} testId="xq-side" />
      <div className="bg-white rounded-3xl border border-gray-200 p-4 md:p-6 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="text-[11px] font-extrabold uppercase tracking-widest text-gray-500" data-testid="xq-progress">Question {idx + 1} of {quiz.questions.length}</span>
          <span className="text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{q.type === 'knowledge' ? 'Learning example' : 'Interest'}</span>
        </div>
        <h3 className="text-base md:text-lg font-bold text-gray-900 leading-snug mb-4 break-words" data-testid="xq-prompt">{q.prompt}</h3>
        <QuizOptions options={opts} selectedId={selected?.id} onSelect={(o) => setAnswers((a) => ({ ...a, [q.id]: o.optionId }))} accent={accent} testIdPrefix={`xq-opt-${q.id}`} />
        <div className="mt-4"><QuestionDots ids={quiz.questions.map((x) => x.id)} answered={new Set(Object.keys(answers))} currentId={q.id} dotClass={accent.dot} /></div>
        {err && <p className="text-sm text-rose-700 mt-3" data-testid="xq-error">{err}</p>}
        <div className="mt-5 flex items-center justify-between gap-2 flex-wrap">
          <button type="button" onClick={() => (idx === 0 ? onBack() : setIdx(idx - 1))} className="inline-flex items-center gap-1 text-sm font-semibold text-gray-600 hover:text-gray-900" data-testid="xq-prev-btn"><ArrowLeft size={14} /> {idx === 0 ? 'All quizzes' : 'Previous'}</button>
          {last ? (
            <button type="button" onClick={submit} disabled={busy} className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-white text-sm font-bold bg-gradient-to-r ${accent.grad} disabled:opacity-60`} data-testid="xq-submit-btn">{busy ? <Loader2 size={14} className="animate-spin" /> : null} See my result</button>
          ) : (
            <button type="button" onClick={() => setIdx(idx + 1)} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-gray-900 text-white text-sm font-bold hover:bg-gray-800" data-testid="xq-next-btn">{answers[q.id] ? 'Next' : 'Skip (not sure)'} <ArrowRight size={14} /></button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Entry: list of quizzes with saved status ── */
export default function ExplorationQuiz({ branch, quizzes, fields, accent }) {
  const [state, setState] = useState(null);
  const [active, setActive] = useState(null); // quiz id
  const [mode, setMode] = useState('list');  // list | run | result

  useEffect(() => {
    axios.get(`${API}/api/career-clarity/guidance/${branch}/quiz/state`).then((r) => setState(r.data.quizzes || {})).catch(() => setState({}));
  }, [branch]);

  const all = useMemo(() => [
    { id: quizzes.id, kind: 'main', title: quizzes.main.title, questions: quizzes.main.questions },
    ...quizzes.optional_mini_quizzes.map((m) => ({ ...m, kind: 'mini' })),
  ], [quizzes]);
  const quiz = all.find((x) => x.id === active);

  if (!state) return <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>;

  if (quiz && mode === 'run') return <Runner branch={branch} quiz={quiz} saved={state[quiz.id]} accent={accent} onBack={() => setMode('list')} onSaved={(doc) => { setState((s) => ({ ...s, [doc.quiz_id]: doc })); setMode('result'); }} />;
  if (quiz && mode === 'result' && state[quiz.id]) return <ResultView saved={state[quiz.id]} quiz={quiz} fields={fields} notice={quizzes.result_notice} onRetake={() => setMode('run')} onBack={() => setMode('list')} />;

  return (
    <div className="space-y-4" data-testid="xq-list">
      <p className="text-sm text-gray-700 leading-relaxed">{quizzes.intro}</p>
      <p className="text-xs text-gray-600 leading-relaxed">{quizzes.presentation_note}</p>
      {all.map((x) => {
        const s = state[x.id];
        return (
          <div key={x.id} className={`rounded-2xl border-2 p-4 flex items-center justify-between gap-3 flex-wrap ${x.kind === 'main' ? 'border-teal-300 bg-teal-50/60' : 'border-gray-200 bg-white'}`} data-testid={`xq-card-${x.id}`}>
            <div className="min-w-0">
              <div className="text-[10px] font-extrabold uppercase tracking-widest text-gray-500">{x.kind === 'main' ? `Main · ${x.questions.length} questions` : `Optional · ${x.questions.length} questions`}</div>
              <div className="font-bold text-gray-900 break-words">{x.title}</div>
              {s && <div className="text-xs text-emerald-700 font-semibold mt-0.5" data-testid={`xq-saved-${x.id}`}>Saved result · attempt {s.attempts}</div>}
            </div>
            <div className="flex gap-2">
              {s && <button type="button" onClick={() => { setActive(x.id); setMode('result'); }} className="px-3.5 py-2 rounded-full bg-white border border-gray-300 text-sm font-bold text-gray-800 hover:bg-gray-50" data-testid={`xq-view-${x.id}`}>View result</button>}
              <button type="button" onClick={() => { setActive(x.id); setMode('run'); }} className={`px-3.5 py-2 rounded-full text-white text-sm font-bold bg-gradient-to-r ${accent.grad}`} data-testid={`xq-start-${x.id}`}>{s ? 'Revise' : 'Start'}</button>
            </div>
          </div>
        );
      })}
      <p className="text-xs text-gray-500 leading-relaxed">{quizzes.national_scope_notice}</p>
    </div>
  );
}
